import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";
import { OptionType } from "commons/ui/input/Select";

import { makeUrl } from "commons/utils/http";

import useShowroomApi from "hooks/api/private/useShowroomApi";
import useSnackbar from "hooks/useSnackbar";

import { ShowroomListItem } from "types/api/showroom";

export type ShowroomListHook = {
    navigateToNewShowroom: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    continentGroup: OptionType[];
    dataTableHook: DataTableHook<ShowroomListItem>;
    staticFilterHook: StaticTableFilterHook;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useShowroomList(): ShowroomListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const [continentGroup, setContinentGroup] = React.useState<OptionType[]>([]);

    const { getAllGroups, deleteShowroom } = useShowroomApi();

    React.useEffect(() => {
        const [apiCall, abortController] = getAllGroups();

        apiCall.then(setContinentGroup);

        return () => abortController.abort();
    }, [getAllGroups]);

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    address_1: {
                        key: "address_1",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    address_2: {
                        key: "address_2",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    virtual_tour_title_1: {
                        key: "virtual_tour_title_1",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    virtual_tour_title_2: {
                        key: "virtual_tour_title_2",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    slug: {
                        key: "slug",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "showroom-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.title = value;
                newForm.search.slug = value;
                newForm.search.address_1 = value;
                newForm.search.address_2 = value;
                newForm.search.virtual_tour_title_1 = value;
                newForm.search.virtual_tour_title_2 = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            group_id: {
                key: "showrooms.group_id",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            enabled: {
                key: "enabled",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "showroom-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllShowroom } = useShowroomApi();

    const navigate = useNavigate();

    const navigateToEditShowroom = React.useCallback(
        (row: ShowroomListItem) => {
            navigate(makeUrl(":showroomId/modifica", { showroomId: row.id }));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Showroom",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteShowroomButton = React.useCallback(
        (row: ShowroomListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare lo Showroom?", () => {
                const [apiCall] = deleteShowroom({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Showroom eliminato con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione dello showroom"));
            });
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteShowroom,
        ],
    );

    const columns: Array<Column<ShowroomListItem>> = React.useMemo(
        (): Array<Column<ShowroomListItem>> => [
            {
                id: "showrooms.graphical_order",
                header: "Ordine",
                width: "85px",
                render: (row: ShowroomListItem) => row.graphical_order,
                orderable: true,
            },
            {
                id: "showrooms_groups_languages.group_name",
                header: "Area geografica",
                width: "150px",
                render: (row: ShowroomListItem) => row.group_name,
                orderable: true,
            },
            {
                id: "showrooms_languages.title",
                header: "Titolo",
                render: (row: ShowroomListItem) => row.title,
                orderable: true,
            },
            {
                id: "enabled",
                header: "Area pubblica",
                width: "130px",
                render: (row: ShowroomListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: ShowroomListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditShowroom}
                        onDelete={deleteShowroomButton}
                    />
                ),
            },
        ],
        [navigateToEditShowroom, deleteShowroomButton],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const extraCallParams = React.useMemo(
        () => ({
            sortAdvanced: JSON.stringify([
                { field: "showrooms_groups.graphical_order", direction: "ASC" },
                { field: "showrooms.graphical_order", direction: "ASC" },
            ]),
        }),
        [],
    );

    const dataTableHook = useDataTableWithMemory<ShowroomListItem>({
        apiCall: getAllShowroom,
        getElements: calculateRows,
        filters: filters,
        extraCallParams: extraCallParams,
        columns: columns,
        size: 20,
        key: "showroom-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewShowroom = React.useCallback(() => {
        navigate("/showroom/nuovo");
    }, [navigate]);

    return React.useMemo(
        (): ShowroomListHook => ({
            navigateToNewShowroom,
            onSearchFilterChange,
            alertConfirmHook,
            staticFilterHook,
            filterSearchHook,
            continentGroup,
            dataTableHook,
        }),
        [
            navigateToNewShowroom,
            onSearchFilterChange,
            alertConfirmHook,
            staticFilterHook,
            filterSearchHook,
            continentGroup,
            dataTableHook,
        ],
    );
}

export default useShowroomList;
